<template>
  <div v-if="visible" class="img-selector-mask" :style="{ zIndex: popupZIndex }">
    <div class="img-selector-modal">
      <header class="img-selector-head">
        <strong>{{ title }}</strong>
        <button @click="close">×</button>
      </header>
      <nav class="img-selector-tabs">
        <button v-if="!isVideo" :class="{ active: activeTab === 'product' }" @click="activeTab = 'product'">
          从商品中获取
        </button>
        <button v-if="!isVideo" :class="{ active: activeTab === 'url' }" @click="activeTab = 'url'">
          输入图片链接
        </button>
        <button
          v-if="!disableUpload"
          :disabled="isVideo && busy"
          :class="{ active: activeTab === 'upload' }"
          @click="activeTab = 'upload'">
          手动上传
        </button>
        <button
          v-if="isVideo && $slots.library"
          :class="{ active: activeTab === 'library' }"
          :disabled="busy"
          @click="activeTab = 'library'">
          店铺素材库
        </button>
      </nav>
      <main class="img-selector-body">
        <div v-if="!isVideo" v-show="activeTab === 'product'" class="tab-product">
          <div v-if="!selectedProduct" class="product-list-view">
            <div class="product-filters">
              <el-select v-model="filters.group_id" class="filter-select" @change="onGroupChange">
                <el-option label="全部分组" value="" />
                <el-option v-for="g in groupList" :key="g.id" :label="g.name || g.group_name" :value="g.id" />
              </el-select>
              <el-select v-model="filters.child_group_id" class="filter-select" @change="loadProducts(1)">
                <el-option label="全部子分组" value="" />
                <el-option
                  v-for="g in childGroupList"
                  :key="g.id"
                  :label="g.name || g.group_name"
                  :value="g.id" />
              </el-select>
              <input v-model="filters.source_product_id" placeholder="来源ID" class="filter-input" />
              <input v-model="filters.keyword" placeholder="商品标题" class="filter-input" />
              <button @click="loadProducts(1)" class="btn-search">查询</button>
            </div>
            <div class="product-grid">
              <div
                v-for="product in productList"
                :key="product.product_id"
                class="product-card"
                @click="selectProduct(product)">
                <img :src="getProductImage(product)" alt="" />
                <div class="product-info">
                  <div class="product-title">{{ product.title || product.product_title }}</div>
                  <div class="product-meta">
                    <span>ID: {{ product.product_id }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="!productList.length" class="empty-state">暂无商品</div>
            <div v-if="page.total > 0" class="pagination">
              <button :disabled="page.page <= 1" @click="loadProducts(page.page - 1)">上一页</button>
              <span>{{ page.page }} / {{ Math.max(1, Math.ceil(page.total / page.limit)) }}</span>
              <button
                :disabled="page.page >= Math.ceil(page.total / page.limit)"
                @click="loadProducts(page.page + 1)">
                下一页
              </button>
            </div>
          </div>
          <div v-else class="product-detail-view">
            <div class="detail-head">
              <button @click="selectedProduct = null" class="btn-back">‹ 返回</button>
              <span>{{ selectedProduct.title || selectedProduct.product_title }}</span>
            </div>
            <div v-if="loadingImages" class="loading-state">加载图片中...</div>
            <div v-else-if="detailImages.length" class="image-grid">
              <div
                v-for="(img, idx) in detailImages"
                :key="idx"
                class="image-card"
                :class="{ selected: isImageSelected(img.url) }"
                @click="toggleImage(img.url)">
                <img :src="img.url" alt="" />
                <div v-if="isImageSelected(img.url)" class="image-check">✓</div>
              </div>
            </div>
            <div v-else class="empty-state">
              {{ imageSource === 'main_sku' ? '该商品暂无主图和SKU图' : '该商品暂无详情图' }}
            </div>
          </div>
        </div>
        <div v-if="!isVideo" v-show="activeTab === 'url'" class="tab-url">
          <textarea
            v-if="multiple"
            v-model="urlText"
            rows="8"
            placeholder="每行一个图片URL例如：
https://example.com/image1.jpg
https://example.com/image2.jpg"
            class="url-textarea" />
          <input
            v-else
            v-model="urlText"
            placeholder="请输入图片URL，例如：
https://example.com/image.jpg"
            class="url-input" />
          <div class="url-tips">
            {{
              sourceSelection
                ? '支持公网 HTTPS 图片地址，先暂存；创建批次并验证商品详情后再上传抖店'
                : '支持 http 或 https 图片地址'
            }}
          </div>
        </div>
        <div v-show="activeTab === 'upload'" class="tab-upload">
          <el-upload
            drag
            action=""
            :multiple="multiple"
            :disabled="isVideo && busy"
            :show-file-list="false"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :http-request="handleDirectUpload"
            :accept="uploadAccept"
            class="upload-area">
            <i class="el-icon-upload"></i>
            <div class="upload-text">
              将{{ mediaLabel }}拖到这里，或
              <em>点击上传</em>
            </div>
            <small v-if="isVideo">MP4视频，最大200MB</small>
          </el-upload>
          <div v-if="sourceSelection" class="staged-images">
            <span v-if="pendingUploads">
              正在暂存 {{ pendingUploads }} {{ isVideo ? '个视频' : '张图片' }}…
            </span>
            <div v-for="(source, index) in stagedSources" :key="source.client_key">
              <video v-if="isVideo" :src="source.preview_url" controls preload="metadata" />
              <img v-else :src="source.preview_url" alt="已暂存图片" />
              <span v-if="isVideo">{{ source.upload_data.object_name }}</span>
              <button :disabled="confirming" @click="stagedSources.splice(index, 1)">移除</button>
            </div>
            <small v-if="stagedSources.length">
              已暂存 {{ stagedSources.length }} {{ isVideo ? '个视频' : '张图片' }}，点击确定后仅登记{{
                mediaLabel
              }}来源；创建批次并验证商品详情后再上传抖店。
            </small>
          </div>
        </div>
        <div v-if="isVideo" v-show="activeTab === 'library'"><slot name="library" /></div>
      </main>
      <footer class="img-selector-footer">
        <button class="btn-cancel" :disabled="isVideo && busy" @click="close">取消</button>
        <button
          class="btn-confirm"
          :disabled="(sourceSelection && pendingUploads > 0) || confirming"
          @click="confirm">
          {{ confirming ? '确认中…' : '确定' }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { cloudProductGroups, cloudProductList } from '@/api/main/ucenter'
import { getProductDetailImages } from '@/api/main/cloudBatchEdit'
import { uploadDirectOss } from '@/assets/js/utils/ossDirectUpload'
import PopupManager from 'element-ui/lib/utils/popup/popup-manager'

export default {
  name: 'MediaSelectorDialog',
  props: {
    visible: Boolean,
    type: String,
    index: Number,
    productId: [String, Number],
    multiple: Boolean,
    disableUpload: {
      type: Boolean,
      default: false
    },
    uploadHandler: {
      type: Function,
      default: null
    },
    uploadAccept: {
      type: String,
      default: 'image/*'
    },
    sourceSelection: Boolean,
    mediaType: { type: String, default: 'image', validator: (value) => ['image', 'video'].includes(value) },
    confirmHandler: { type: Function, default: null },
    imageSource: {
      type: String,
      default: 'detail' // 'detail' 或 'main_sku' (主图+SKU图)
    }
  },
  data() {
    return {
      popupZIndex: PopupManager.nextZIndex(),
      activeTab: 'product',
      stagedSources: [],
      pendingUploads: 0,
      confirming: false,
      uploadGeneration: 0,
      groupList: [],
      childGroupList: [],
      filters: {
        group_id: '',
        child_group_id: '',
        source_product_id: '',
        keyword: ''
      },
      productList: [],
      page: { page: 1, limit: 20, total: 0 },
      selectedProduct: null,
      detailImages: [],
      loadingImages: false,
      selectedImages: [],
      urlText: ''
    }
  },
  computed: {
    isVideo() {
      return this.mediaType === 'video'
    },
    mediaLabel() {
      return this.isVideo ? '视频' : '图片'
    },
    busy() {
      return this.pendingUploads > 0 || this.confirming
    },
    title() {
      if (this.isVideo) return '选择主图视频'
      const typeMap = {
        filter: '选择过滤图片',
        replace_find: '选择查找图片',
        replace_to: '选择替换图片',
        add_head: '选择首部图片',
        add_tail: '选择尾部图片',
        add_position: '选择插入图片',
        delete_all_add: '选择新增图片',
        inline_add: '添加图片'
      }
      return typeMap[this.type] || '选择图片'
    }
  },
  watch: {
    activeTab(value) {
      this.$emit('tab-change', value)
    },
    visible(val) {
      if (val) {
        this.popupZIndex = PopupManager.nextZIndex()
        this.reset()
        if (!this.isVideo) {
          this.loadGroups()
          this.loadProducts(1)
        }
      } else {
        this.uploadGeneration++
        this.stagedSources = []
        this.pendingUploads = 0
        // 弹窗关闭时清空所有状态
        this.activeTab = 'product'
        this.selectedProduct = null
        this.selectedImages = []
        this.urlText = ''
        this.detailImages = []
        this.productList = []
        this.filters = {
          group_id: '',
          child_group_id: '',
          source_product_id: '',
          keyword: ''
        }
        this.page = { page: 1, limit: 20, total: 0 }
      }
    }
  },
  methods: {
    reset() {
      this.uploadGeneration++
      this.stagedSources = []
      this.pendingUploads = 0
      this.activeTab = this.isVideo ? 'upload' : 'product'
      this.selectedProduct = null
      this.selectedImages = []
      this.urlText = ''
      this.detailImages = []
    },
    async loadGroups() {
      try {
        const r = await cloudProductGroups({ with_count: 0 })
        this.groupList = (r.data && (r.data.data || r.data)) || []
      } catch (e) {
        this.$message.error('分组加载失败')
      }
    },
    onGroupChange() {
      const g = this.groupList.find((i) => String(i.id) === String(this.filters.group_id))
      this.childGroupList = (g && g.child_groups) || []
      this.filters.child_group_id = ''
      this.loadProducts(1)
    },
    async loadProducts(page = 1) {
      try {
        const r = await cloudProductList({
          page,
          page_size: this.page.limit,
          status: 1,
          ...this.filters
        })
        const d = (r.data && (r.data.data || r.data)) || {}
        this.productList = d.list || d.data || []
        this.page = { ...this.page, page, total: Number(d.total || 0) }
      } catch (e) {
        this.$message.error('商品查询失败')
      }
    },
    async selectProduct(product) {
      this.selectedProduct = product
      this.loadingImages = true
      try {
        const imageType = this.imageSource === 'main_sku' ? 'main_sku' : 'detail'
        const r = await getProductDetailImages(product.product_id, imageType)
        if (r.code === 200 || r.code === 0) {
          if (this.imageSource === 'main_sku') {
            // 主图模式：展示主图和SKU图，去重
            const allImages = []
            const seenUrls = new Set()

            // 添加主图
            const mainImages = r.data.main_images || []
            mainImages.forEach((img) => {
              if (img.url && !seenUrls.has(img.url)) {
                seenUrls.add(img.url)
                allImages.push({
                  id: img.id,
                  url: img.url,
                  sort: img.sort,
                  type: 'main'
                })
              }
            })

            // 添加SKU图
            const skuImages = r.data.sku_images || []
            skuImages.forEach((img) => {
              if (img.url && !seenUrls.has(img.url)) {
                seenUrls.add(img.url)
                allImages.push({
                  id: img.id,
                  url: img.url,
                  sort: img.sort,
                  type: 'sku'
                })
              }
            })

            this.detailImages = allImages
          } else {
            // 详情图模式：只展示详情图
            this.detailImages = (r.data.detail_images || []).map((img) => ({
              id: img.id,
              url: img.url,
              sort: img.sort
            }))
          }
        } else {
          throw new Error(r.msg || '加载图片失败')
        }
      } catch (e) {
        this.$message.error(e.message || '加载图片失败')
        this.detailImages = []
      } finally {
        this.loadingImages = false
      }
    },
    getProductImage(product) {
      return (
        product.main_image ||
        product.image_url ||
        (Array.isArray(product.main_images) && product.main_images[0]) ||
        ''
      )
    },
    isImageSelected(url) {
      return this.selectedImages.includes(url)
    },
    toggleImage(url) {
      const idx = this.selectedImages.indexOf(url)
      if (idx >= 0) {
        this.selectedImages.splice(idx, 1)
      } else {
        if (!this.multiple) {
          this.selectedImages = [url]
        } else {
          this.selectedImages.push(url)
        }
      }
    },
    handleDirectUpload(options) {
      // Element Upload会对返回的Promise再次调用成功回调；此处使用回调模式，避免重复暂存。
      this.performUpload(options)
    },
    async performUpload(options) {
      const generation = this.uploadGeneration
      if (this.sourceSelection) this.pendingUploads++
      try {
        const file = options.file
        if (
          this.isVideo
            ? !file || !/\.mp4$/i.test(file.name) || !file.size || file.size > 200 * 1024 * 1024
            : !file || !file.type || !file.type.startsWith('image/')
        ) {
          throw new Error(this.isVideo ? '请选择200MB以内的MP4视频' : '请选择图片文件')
        }
        if (this.isVideo && !this.uploadHandler) throw new Error('缺少视频上传处理器')
        const uploadOptions = { onProgress: (event) => options.onProgress && options.onProgress(event) }
        const data = this.uploadHandler
          ? await this.uploadHandler(file, uploadOptions)
          : await uploadDirectOss(file, { ...uploadOptions, purpose: 'merchant_image' })
        if (generation === this.uploadGeneration && this.visible)
          options.onSuccess && options.onSuccess({ code: 200, data })
      } catch (error) {
        if (generation === this.uploadGeneration && this.visible) options.onError && options.onError(error)
      } finally {
        if (this.sourceSelection && generation === this.uploadGeneration) this.pendingUploads--
      }
    },
    handleUploadSuccess(res) {
      const url =
        res && res.code === 200 ? res.data.address || res.data.url || res.data.storage_path || '' : ''
      if (!url) {
        this.$message.error(res?.message || res?.msg || '上传失败')
        return
      }
      if (this.sourceSelection) {
        const value = this.sourceValue(url, 'upload')
        const source = this.isVideo ? { ...value, upload_data: res.data } : value
        this.stagedSources = this.multiple ? [...this.stagedSources, source] : [source]
        return
      }
      this.$emit('select', [url])
      this.$message.success('上传成功')
      this.close()
    },
    handleUploadError(error) {
      this.$message.error(error && error.message ? error.message : '上传失败')
    },
    async confirm() {
      if (this.isVideo && this.confirmHandler) {
        if (this.busy) return
        if (this.activeTab === 'upload' && !this.stagedSources.length)
          return this.$message.warning('请先上传视频')
        this.confirming = true
        try {
          await this.confirmHandler({ tab: this.activeTab, sources: [...this.stagedSources] })
          this.$emit('update:visible', false)
        } catch (error) {
          this.$message.error(error.message || '视频选择失败，请重试')
        } finally {
          this.confirming = false
        }
        return
      }
      if (this.sourceSelection && this.pendingUploads) return this.$message.warning('请等待图片暂存完成')
      if (this.sourceSelection && this.activeTab === 'upload') {
        if (!this.stagedSources.length) return this.$message.warning('请先上传图片')
        this.$emit('source-select', [...this.stagedSources])
        this.close()
        return
      }
      if (this.activeTab === 'product') {
        if (!this.selectedImages.length) {
          this.$message.warning('请选择图片')
          return
        }
        this.emitImages(this.selectedImages)
        this.close()
      } else if (this.activeTab === 'url') {
        const urls = this.urlText
          .split(/[\n,，]/)
          .map((item) => item.trim())
          .filter(Boolean)

        if (!urls.length) {
          this.$message.warning('请填写图片URL')
          return
        }
        const invalid = urls.find(
          (url) => !(this.sourceSelection ? /^https:\/\/.+/i : /^https?:\/\/.+/i).test(url)
        )
        if (invalid) {
          this.$message.warning(
            this.sourceSelection ? '抖店图片URL仅支持公网HTTPS地址' : '图片URL仅支持http或https'
          )
          return
        }
        this.emitImages(this.multiple ? urls : [urls[0]])
        this.close()
      }
    },
    sourceValue(url, kind = 'url') {
      const key = (
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`
      ).replace(/[^a-z0-9-]/gi, '')
      return { client_key: key, source_kind: kind, source_ref: url, preview_url: url }
    },
    emitImages(urls) {
      if (this.sourceSelection)
        this.$emit(
          'source-select',
          urls.map((url) => this.sourceValue(url))
        )
      else this.$emit('select', urls)
    },
    close() {
      if (this.isVideo && this.busy) return
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.staged-images {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
}
.staged-images > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.staged-images img {
  width: 80px;
  height: 80px;
  object-fit: cover;
}
.staged-images video {
  width: 160px;
  max-height: 120px;
}
.staged-images > small {
  flex-basis: 100%;
  color: #909399;
}
.img-selector-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.img-selector-modal {
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  background: #fff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.img-selector-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #e8e8e8;
}

.img-selector-head strong {
  font-size: 16px;
  font-weight: 500;
}

.img-selector-head button {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.img-selector-head button:hover {
  color: #333;
}

.img-selector-tabs {
  display: flex;
  padding: 0 24px;
  border-bottom: 1px solid #e8e8e8;
  gap: 8px;
}

.img-selector-tabs button {
  padding: 12px 20px;
  border: none;
  background: transparent;
  color: #666;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}

.img-selector-tabs button:hover {
  color: #1677ff;
}

.img-selector-tabs button.active {
  color: #1677ff;
  font-weight: 500;
}

.img-selector-tabs button.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #1677ff;
}

.img-selector-body {
  flex: 1;
  overflow: auto;
  padding: 24px;
  min-height: 400px;
}

.product-filters {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.filter-select {
  width: 160px;
}

.filter-input {
  padding: 0 12px;
  height: 32px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  width: 160px;
  font-size: 14px;
  line-height: 32px;
}

.filter-input:focus {
  outline: none;
  border-color: #1677ff;
}

.btn-search {
  padding: 0 16px;
  height: 32px;
  border: 1px solid #1677ff;
  background: #1677ff;
  color: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.btn-search:hover {
  background: #0958d9;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
}

.product-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.product-card:hover {
  border-color: #1677ff;
  box-shadow: 0 2px 8px rgba(22, 119, 255, 0.1);
}

.product-card img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  border-radius: 4px;
  margin-bottom: 8px;
}

.product-info {
  font-size: 12px;
}

.product-title {
  color: #333;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
  height: 2.8em;
}

.product-meta {
  color: #999;
  font-size: 11px;
}

.detail-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8e8e8;
}

.btn-back {
  padding: 4px 12px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
}

.btn-back:hover {
  border-color: #1677ff;
  color: #1677ff;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.image-card {
  position: relative;
  aspect-ratio: 1;
  border: 2px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s;
}

.image-card:hover {
  border-color: #1677ff;
}

.image-card.selected {
  border-color: #1677ff;
}

.image-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-check {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background: #1677ff;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.tab-url {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.url-textarea,
.url-input {
  padding: 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-family: inherit;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}

.url-textarea:focus,
.url-input:focus {
  outline: none;
  border-color: #1677ff;
}

.url-tips {
  color: #909399;
  font-size: 12px;
}

.tab-upload {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}

.upload-area {
  width: 100%;
  max-width: 400px;
}

.upload-text em {
  color: #1677ff;
  font-style: normal;
}

.empty-state,
.loading-state {
  text-align: center;
  color: #999;
  padding: 60px 0;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
}

.pagination button {
  padding: 4px 12px;
  border: 1px solid #d9d9d9;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.pagination button:hover:not(:disabled) {
  border-color: #1677ff;
  color: #1677ff;
}

.pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.img-selector-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e8e8e8;
}

.btn-cancel,
.btn-confirm {
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-cancel {
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #666;
}

.btn-cancel:hover {
  border-color: #1677ff;
  color: #1677ff;
}

.btn-confirm {
  border: 1px solid #1677ff;
  background: #1677ff;
  color: #fff;
}

.btn-confirm:hover {
  background: #0958d9;
}
</style>
